import { Component } from '@theme/component';

/**
 * Ola · Para cada ritmo.
 *
 * - Celular y tablet (menos de 990 px): los paneles son un carrusel con desplazamiento
 *   nativo. Los puntos marcan el panel visible y llevan a cada uno.
 * - Escritorio: el panel con el cursor o el foco se marca con `data-active` y el CSS lo abre.
 * - Avance automático (`data-autoplay="true"`): cada `data-interval` ms pasa al siguiente
 *   panel. Solo corre con la sección en pantalla y la pestaña visible; se detiene mientras
 *   el cursor o el foco están dentro, se apaga del todo cuando la persona toca el carrusel o
 *   usa el botón de pausa, y no corre con movimiento reducido.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} list - Lista de paneles.
 * @property {HTMLElement[]} panels - Paneles (enlaces), con `data-index`.
 * @property {HTMLButtonElement[]} [dots] - Puntos del carrusel.
 * @property {HTMLElement} [controls] - Contenedor de los puntos y la pausa.
 * @property {HTMLButtonElement} [pause] - Botón de pausa del avance automático.
 *
 * @extends {Component<Refs>}
 */
export class OlaRitmos extends Component {
  requiredRefs = ['list', 'panels'];

  /** Ancho desde el que todos los paneles están a la vista. */
  static DESKTOP_QUERY = '(min-width: 990px)';

  /** @type {AbortController | undefined} */
  #controller;

  /** @type {IntersectionObserver | undefined} */
  #panelObserver;

  /** @type {IntersectionObserver | undefined} */
  #sectionObserver;

  /** @type {MediaQueryList | undefined} */
  #desktop;

  /** @type {number | undefined} */
  #timer;

  /** @type {number | undefined} */
  #scrollTimer;

  /** Paneles casi enteros a la vista en el carrusel. @type {Set<number>} */
  #visible = new Set();

  #index = 0;

  /** La persona apagó el avance automático (pausa o toque). */
  #stopped = false;

  /** Cursor o foco dentro de la sección. */
  #holding = false;

  #inView = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  /**
   * Marca el panel como activo (escritorio). Se llama desde `on:pointerenter` y `on:focus`.
   * @param {number} index
   */
  activate(index) {
    if (!this.#desktop?.matches) return;
    this.#setActive(index);
  }

  /**
   * Lleva al panel elegido. Se llama desde los puntos.
   * @param {number} index
   */
  goTo(index) {
    this.#stop();
    this.#setActive(index);
    this.#scrollTo(index);
  }

  /** Pausa o reanuda el avance automático. */
  togglePause() {
    if (this.#stopped) {
      this.#stopped = false;
      this.#updatePauseButton();
      this.#schedule();
    } else {
      this.#stop();
    }
  }

  #setup() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#desktop = window.matchMedia(OlaRitmos.DESKTOP_QUERY);
    this.#index = Math.max(
      0,
      this.refs.panels.findIndex((panel) => panel.hasAttribute('data-active'))
    );

    if (this.refs.controls) this.refs.controls.hidden = false;

    // Carrusel: el activo es el primero de los paneles casi enteros a la vista (en tablet se
    // ven dos). Se recalcula también al terminar de desplazarse, para detectar el final.
    this.#visible = new Set();
    this.#panelObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!(entry.target instanceof HTMLElement)) continue;
          const index = Number(entry.target.dataset.index);
          if (entry.isIntersecting) this.#visible.add(index);
          else this.#visible.delete(index);
        }
        this.#syncWithScroll();
      },
      { root: this.refs.list, threshold: 0.6 }
    );
    for (const panel of this.refs.panels) this.#panelObserver.observe(panel);
    this.refs.list.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(this.#scrollTimer);
        this.#scrollTimer = window.setTimeout(() => this.#syncWithScroll(), 120);
      },
      { passive: true, signal }
    );

    if (this.dataset.autoplay !== 'true' || this.refs.panels.length < 2) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.#stopped = true;
      this.#updatePauseButton();
      return;
    }

    this.#sectionObserver = new IntersectionObserver(
      ([entry]) => {
        this.#inView = Boolean(entry?.isIntersecting);
        this.#schedule();
      },
      { threshold: 0.4 }
    );
    this.#sectionObserver.observe(this);

    this.addEventListener('pointerenter', () => this.#hold(true), { signal });
    this.addEventListener('pointerleave', () => this.#hold(false), { signal });
    this.addEventListener('focusin', () => this.#hold(true), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
        this.#hold(false);
      },
      { signal }
    );
    // Si la persona desliza el carrusel, el avance automático se apaga.
    this.refs.list.addEventListener('touchstart', () => this.#stop(), { passive: true, signal });
    this.refs.list.addEventListener('wheel', () => this.#stop(), { passive: true, signal });
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
  }

  #teardown() {
    this.#controller?.abort();
    this.#panelObserver?.disconnect();
    this.#sectionObserver?.disconnect();
    window.clearTimeout(this.#timer);
    window.clearTimeout(this.#scrollTimer);
    this.#timer = undefined;
  }

  /** Marca como activo el panel visible del carrusel (celular y tablet). */
  #syncWithScroll() {
    if (this.#desktop?.matches || this.#visible.size === 0) return;

    // Al final del carrusel el último panel no puede quedar al inicio: cuenta el último visible.
    const { list } = this.refs;
    const atEnd = list.scrollWidth - Math.abs(list.scrollLeft) - list.clientWidth < 2;
    this.#setActive(atEnd ? Math.max(...this.#visible) : Math.min(...this.#visible));
  }

  /** @param {boolean} holding */
  #hold(holding) {
    this.#holding = holding;
    this.#schedule();
  }

  #stop() {
    if (this.dataset.autoplay !== 'true') return;
    this.#stopped = true;
    window.clearTimeout(this.#timer);
    this.#timer = undefined;
    this.#updatePauseButton();
  }

  #schedule() {
    window.clearTimeout(this.#timer);
    this.#timer = undefined;

    if (this.#stopped || this.#holding || !this.#inView || document.hidden) return;

    const interval = Number(this.dataset.interval) || 6000;
    this.#timer = window.setTimeout(() => {
      const next = (this.#index + 1) % this.refs.panels.length;
      this.#setActive(next);
      this.#scrollTo(next);
      this.#schedule();
    }, interval);
  }

  /** @param {number} index */
  #setActive(index) {
    const { panels, dots = [] } = this.refs;
    if (!Number.isInteger(index) || index < 0 || index >= panels.length) return;

    this.#index = index;
    panels.forEach((panel, i) => panel.toggleAttribute('data-active', i === index));
    dots.forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }

  /**
   * Desplaza el carrusel (celular y tablet) hasta el panel, sin mover la página.
   * @param {number} index
   */
  #scrollTo(index) {
    if (this.#desktop?.matches) return;

    const { list, panels } = this.refs;
    const item = panels[index]?.parentElement;
    if (!item) return;

    // scrollIntoView podría mover la página; se desplaza solo la lista.
    const style = getComputedStyle(list);
    const padding = parseFloat(style.scrollPaddingInlineStart) || 0;
    const itemRect = item.getBoundingClientRect();
    const listRect = list.getBoundingClientRect();
    const delta =
      style.direction === 'rtl'
        ? itemRect.right - (listRect.right - padding)
        : itemRect.left - (listRect.left + padding);

    list.scrollBy({
      left: delta,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  #updatePauseButton() {
    const button = this.refs.pause;
    if (!button) return;

    button.setAttribute('aria-pressed', String(this.#stopped));
    const label = this.#stopped ? button.dataset.labelPlay : button.dataset.labelPause;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('ola-ritmos')) {
  customElements.define('ola-ritmos', OlaRitmos);
}
